/* eslint-disable no-unused-vars */
/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import {
	FORM_NAME,
	FORM_SETTINGS_ADMIN_NAME,
	FORM_ABOUT_NAME,
	FORM_SETTINGS_NAME,
	TABLE_ROWS_NAMES,
	TABLE_TAX_ROWS_NAMES,
	TABLE_ROWS_ABOUT_NAMES,
} from './constants';
import { normalizePhone } from './utils';

export const selectCompaniesModalDomain = state => state.companiesModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};

export const getAboutFormIsInvalidSelector = () => createSelector(isInvalid(FORM_ABOUT_NAME), isInvalid => isInvalid);

export const getCompanyInfoSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfo }) => {
		if (!companyInfo) {
			return [];
		}

		const data = {
			type: companyInfo.type?.name || '-',
			inn: companyInfo.inn || '-',
			ogrn: companyInfo.ogrn || '-',
			address: companyInfo.address || '-',
			...(companyInfo.contract !== null ? { contract: companyInfo.contract || 'Нет' } : {}),
			...(companyInfo.saas !== null ? { saas: companyInfo.saas || 'Нет' } : {}),
			...(companyInfo.is_accredited !== null ? { is_accredited: companyInfo.is_accredited ? 'Да' : 'Нет' } : {}),
			...(companyInfo.is_validated !== null ? { is_validated: companyInfo.is_validated ? 'Да' : 'Нет' } : {}),
			...(companyInfo.has_contract !== null ? { has_contract: companyInfo.has_contract ? 'Да' : 'Нет' } : {}),
			...(companyInfo.has_saas !== null ? { has_saas: companyInfo.has_saas ? 'Да' : 'Нет' } : {}),
		};

		return Object.entries(data).reduce((result, [key, value]) => {
			return [...result, { id: key, title: TABLE_ROWS_NAMES[key], value }];
		}, []);
	});

export const getFormInitialCompanyAboutValuesSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfoAbout }) => {
		if (!companyInfoAbout) {
			return {};
		}

		return {
			description: companyInfoAbout.description || '',
			website: companyInfoAbout.website || '',
			delivery_methods: companyInfoAbout.delivery_methods?.map(method => method.id) || [],
			transports: companyInfoAbout.transports?.map((transport, index) => ({
				car_type_id: transport.car_type?.id || '',
				tonnage_id: transport.tonnage?.id || 'none',
				quantity: transport.quantity || '',
			})) || [{ car_type_id: '', tonnage_id: '', quantity: '' }],
			modes: companyInfoAbout.modes?.map(mode => mode.id) || [],
			has_validated_transport: companyInfoAbout.has_validated_transport || false,
			has_qms: companyInfoAbout.has_qms || false,
			contact: {
				post: companyInfoAbout.contact?.post || '',
				last_name: companyInfoAbout.contact?.last_name || '',
				first_name: companyInfoAbout.contact?.first_name || '',
				email: companyInfoAbout.contact?.email || '',
				phone: companyInfoAbout.contact?.phone || '',
				extension: companyInfoAbout.contact?.extension || '',
			},
		};
	});

export const getCompanyInfoAboutSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfoAbout }) => {
		if (!companyInfoAbout) {
			return [];
		}

		const data = companyInfoAbout;

		const formatBoolean = value => {
			if (value === true) return 'Да';
			if (value === false) return 'Нет';
			return '---';
		};

		const result = {
			description: data.description || '---',
			website: data.website || '---',
			delivery_methods:
				data.delivery_methods?.length > 0 ? data.delivery_methods.map(item => item.name).join(', ') : '---',
			transports: data.transports?.length > 0 ? data.transports : '---',
			modes: data.modes?.length > 0 ? data.modes.map(item => `${item.name}°C`).join(', ') : '---',
			has_validated_transport: formatBoolean(data.has_validated_transport),
			has_qms: formatBoolean(data.has_qms),
			contact: data.contact
				? {
						last_name: data.contact?.last_name || '',
						first_name: data.contact?.first_name || '',
						middle_name: data.contact?.middle_name || '',
						email: data.contact?.email || '',
						phone: data.contact?.phone || '',
						extension: data.contact?.extension || '',
						post: data.contact?.post || '',
				  }
				: '---',
		};

		return Object.entries(result).reduce((acc, [key, value]) => {
			return [...acc, { id: key, title: TABLE_ROWS_ABOUT_NAMES[key], value }];
		}, []);
	});

export const getCompanyTaxSystemInfoSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfo }) => {
		if (!companyInfo) {
			return [];
		}

		const data = {
			org_type: companyInfo.org_type?.name || '---',
			tax_system: companyInfo.tax_system?.name || '---',
			tax_rate: companyInfo.tax_rate?.name || '---',
		};

		return Object.entries(data).map(([key, value]) => ({
			id: key,
			title: TABLE_TAX_ROWS_NAMES[key],
			// eslint-disable-next-line no-nested-ternary
			value: key === 'tax_rate' ? (value !== '---' ? `${value}%` : value) : value,
		}));
	});

export const getCompanyContactsInfoSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfo }) => {
		if (!companyInfo) {
			return [];
		}

		const data = {
			...(companyInfo.contact !== null
				? {
						contact: {
							first_name: companyInfo.contact?.first_name || '-',
							last_name: companyInfo.contact?.last_name || '-',
							middle_name: companyInfo.contact?.middle_name,
							phone: companyInfo.contact?.phone || '-',
							email: companyInfo.contact?.email || '-',
							extension: companyInfo.contact?.extension || '',
						},
				  }
				: null),
		};

		return Object.entries(data).reduce((result, [key, value]) => {
			return [...result, { id: key, title: TABLE_ROWS_NAMES[key], value }];
		}, []);
	});

export const getIsCompanyInfoLoadingSelector = () =>
	createSelector(
		selectCompaniesModalDomain,
		({ isCompanyInfoLoading, isCompanySaving }) => isCompanyInfoLoading || isCompanySaving,
	);

export const getCompanyNameSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfo }) => companyInfo?.name || '');

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && (query?.modal === 'company' || query?.modal === 'profile_company'));
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && query?.tab === 'audit');
	});
export const getIsSettingTabShownSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && query?.tab === 'settings');
	});
export const getIsAboutTabShownSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && query?.tab === 'about');
	});
export const getActiveCompanyIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'company' || query?.modal === 'profile_company' ? query?.id : null;
	});

export const getIsCompanyEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && Boolean(query?.id) && query?.mode === 'edit');
	});

export const getIsCompanyAboutEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && Boolean(query?.id) && query?.mode === 'about-company-edit');
	});

export const getIsProfileCompanyFormSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && query?.modal === 'profile_company');
	});

export const getFormInitialValuesSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfo }) => {
		if (!companyInfo) {
			return {};
		}

		return {
			company: {
				type_id: companyInfo.type?.id || '',
				name: companyInfo.name || '',
				inn: companyInfo.inn || '',
				ogrn: companyInfo.ogrn || '',
				address: companyInfo.address || '',
				contract_number: companyInfo.contract_number || '',
				contract_date: companyInfo.contract_date || '',
				saas_number: companyInfo.saas_number || '',
				saas_date: companyInfo.saas_date || '',
				is_accredited: companyInfo.is_accredited || '',
				is_validated: companyInfo.is_validated || '',
				has_contract: companyInfo.has_contract || '',
				has_saas: companyInfo.has_saas || '',
				org_type_id: companyInfo.org_type?.id || '',
				tax_system_id: companyInfo.tax_system?.id || '',
				tax_rate_id: companyInfo.tax_rate?.id || '',
			},
			contact: {
				first_name: companyInfo.contact?.first_name || '',
				last_name: companyInfo.contact?.last_name || '',
				middle_name: companyInfo.contact?.middle_name || '',
				phone: companyInfo.contact?.phone ? normalizePhone(companyInfo.contact?.phone) : '',
				extension: companyInfo.contact?.extension || '',
				email: companyInfo.contact?.email || '',
			},
		};
	});

export const getDataForUpdateCompanySelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		if (!formValues) return formValues;

		return {
			...formValues,
			company: {
				...(formValues.company || {}),
				org_type_id: formValues.company?.org_type_id || null,
				tax_system_id: formValues.company?.tax_system_id || null,
				tax_rate_id: formValues.company?.tax_rate_id || null,
			},
			contact: {
				...formValues.contact,
			},
		};
	});
export const getIsCompaniesTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompaniesTypesLoading }) => isCompaniesTypesLoading);

export const getCompaniesTypesSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companiesTypes }) => companiesTypes);

export const getCompaniesModeSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ modesTypes }) => modesTypes);
export const getCompaniesTonnageSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ tonnagesTypes }) => tonnagesTypes);
export const getCompaniesDeliverySelector = () =>
	createSelector(selectCompaniesModalDomain, ({ deliveryTypes }) => deliveryTypes);
export const getCompaniesCarSelector = () => createSelector(selectCompaniesModalDomain, ({ carTypes }) => carTypes);

export const getCompaniesSelector = () => createSelector(selectCompaniesModalDomain, ({ companies }) => companies);

export const getCompaniesSearchValueSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companiesSearchValue }) => companiesSearchValue);

export const getCompaniesOptionsSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companies }) =>
		companies.map(company => ({
			key: `${company.data.inn}${company.data.kpp}`,
			value: company.data.inn,
			data: {
				title: company.value,
				address: `${company.data?.kpp || ''} ${company.data?.address?.value || ''}`,
				deleted: company.data?.state?.status === 'LIQUIDATED',
			},
		})),
	);

export const getIsCompaniesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompaniesLoading }) => isCompaniesLoading);

export const getIsCompaniesPatchLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompaniesPatchLoading }) => isCompaniesPatchLoading);

export const getOrgTypesSelector = () => createSelector(selectCompaniesModalDomain, ({ orgTypes }) => orgTypes);

export const getIsOrgTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isOrgTypesLoading }) => isOrgTypesLoading);

export const getIsCarTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCarTypesLoading }) => isCarTypesLoading);

export const getIsDeliveryTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isDeliveryTypesLoading }) => isDeliveryTypesLoading);

export const getIsTonnagesTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isTonnagesTypesLoading }) => isTonnagesTypesLoading);

export const getIsModesTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isModesTypesLoading }) => isModesTypesLoading);

export const getIsAllCompanyReferenceDataLoadedSelector = () =>
	createSelector(
		selectCompaniesModalDomain,
		({ isCarTypesLoading, isDeliveryTypesLoading, isTonnagesTypesLoading, isModesTypesLoading }) => {
			return isCarTypesLoading && isDeliveryTypesLoading && isTonnagesTypesLoading && isModesTypesLoading;
		},
	);

export const getTaxSystemsSelector = () => createSelector(selectCompaniesModalDomain, ({ taxSystems }) => taxSystems);

export const getIsTaxSystemsLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isTaxSystemsLoading }) => isTaxSystemsLoading);

export const getTaxRatesSelector = () => createSelector(selectCompaniesModalDomain, ({ taxRates }) => taxRates);

export const getIsTaxRatesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isTaxRatesLoading }) => isTaxRatesLoading);

export const getCompaniesTabsSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyInfo }) => companyInfo?.tabs || {});

export const getCompanySettingInfoSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companySettingInfo, stages, documentTypes }) => {
		if (!companySettingInfo) {
			return {};
		}

		const result = {
			data: {
				transports: companySettingInfo.transports,
				my_transports: companySettingInfo.my_transports,
				stages: [],
				documents: [],
			},
		};

		if (stages) {
			result.data.stages = stages.map(stage => {
				const foundStage = companySettingInfo.stages?.find(s => s.key === stage.key);

				return {
					key: stage.key,
					name: stage.name,
					value: foundStage ? foundStage.value : 0,
				};
			});
		}

		const isDoneEnabled = result.data.stages.some(stage => stage.key === 'done' && stage.value === true);

		if (isDoneEnabled && companySettingInfo.documents.length && documentTypes) {
			result.data.documents = companySettingInfo.documents.map(doc => {
				const documentType = documentTypes.find(d => d.key === doc.key);

				return {
					key: doc.key,
					name: doc.key === '8-other' ? doc.name : documentType?.name || 'Неизвестный документ',
				};
			});
		}

		return result;
	});
export const getIsCompanySettingLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompanySettingLoading }) => isCompanySettingLoading);

export const getIsCompanySettingSavingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompanySettingSaving }) => isCompanySettingSaving);

export const getStagesSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ stages }) => {
		return stages;
	});

export const getIsStagesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isStagesLoading }) => isStagesLoading);

export const getDocumentTypesSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ documentTypes }) => documentTypes);

export const getDocumentTypesOptionsSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ documentTypes }) => {
		if (documentTypes) {
			return documentTypes.map(doc => ({
				key: doc.key,
				value: doc.key,
				text: doc.name,
			}));
		}

		return [];
	});

export const getIsDocumentsTypesLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isDocumentsTypesLoading }) => isDocumentsTypesLoading);

export const getFormInitialCompanySettingValuesSelector = () =>
	createSelector(
		selectCompaniesModalDomain,
		getFormValues(FORM_NAME),
		getIsStagesLoadingSelector(),
		({ stages, companySettingInfo, documentTypes }, formValues, isStagesLoading) => {
			if (!companySettingInfo) {
				if (isStagesLoading || !stages?.length) {
					return {};
				}

				const defaultStageValues = stages.reduce((acc, stage) => {
					acc[stage.key] = stage.key !== 'done';
					return acc;
				}, {});

				return {
					stages: defaultStageValues,
					documents: [],
				};
			}

			const defaultStageKeys = [
				'new',
				'approved',
				'select_company',
				'selected_company',
				'collected',
				'set_driver',
				'set_car',
				'fetch',
				'done',
			];

			const defaultStageValues = defaultStageKeys.reduce((acc, key) => {
				if (key === 'done') {
					acc[key] = formValues?.[key] ?? false;
				} else {
					acc[key] = formValues?.[key] ?? true;
				}

				return acc;
			}, {});

			const transformedStages = companySettingInfo?.stages?.reduce((acc, { key, value }) => {
				acc[key] = value === true;
				return acc;
			}, {});

			const stagesValues = companySettingInfo
				? { ...defaultStageValues, ...transformedStages }
				: defaultStageValues;

			const documents = companySettingInfo
				? companySettingInfo.documents?.map(doc => {
						const documentType = documentTypes?.find(d => d.key === doc.key);

						if (doc.key === '8-other') {
							const otherDoc = companySettingInfo.documents.find(d => d.key === '8-other');

							return {
								key: doc.key,
								label: doc.key,
								name: otherDoc?.name || '',
							};
						}

						return {
							key: doc.key,
							label: doc.key,
							name: documentType?.text || '',
						};
				  }) || []
				: [];

			return {
				transports: companySettingInfo.transports,
				my_transports: companySettingInfo.my_transports,
				stages: stagesValues,
				documents,
			};
		},
	);

export const getDataForUpdateCompanySettingsSelector = () =>
	createSelector([getFormValues(FORM_SETTINGS_NAME), getDocumentTypesSelector()], (formValues, documentTypes) => {
		const { transports, stages = {}, documents = [], my_transports } = formValues || {};

		const filteredDocuments = documents.filter(doc => doc.key);

		return {
			transports,
			my_transports,
			stages: Object.entries(stages).map(([key, value]) => ({ key, value })),
			documents: filteredDocuments.map(doc => {
				const fullDoc = documentTypes.find(d => d.key === doc.key) || {};

				const baseDoc = { key: doc.key };

				if (doc.key === '8-other') {
					baseDoc.name = doc.name || fullDoc.name || doc.key;
				}

				return baseDoc;
			}),
		};
	});
export const getIsCompanySettingEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && Boolean(query?.id) && query?.mode === 'settings-edit');
	});
export const getCompanyAdminSettingInfoSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyAdminSettingInfo }) => {
		if (!companyAdminSettingInfo) {
			return {};
		}

		const result = {
			data: {
				transports: companyAdminSettingInfo.transports,
				claims: companyAdminSettingInfo.claims,
			},
		};

		return result;
	});
export const getIsCompanyAdminSettingLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompanyAdminSettingLoading }) => isCompanyAdminSettingLoading);

export const getIsCompanyAdminSettingSavingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompanyAdminSettingSaving }) => isCompanyAdminSettingSaving);

export const getIsCompanyAboutLoadingSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ isCompanyAboutLoading }) => isCompanyAboutLoading);

export const getFormInitialCompanyAdminSettingValuesSelector = () =>
	createSelector(selectCompaniesModalDomain, ({ companyAdminSettingInfo }) => {
		if (!companyAdminSettingInfo) {
			return {};
		}

		return {
			transports: companyAdminSettingInfo.transports,
			my_transports: companyAdminSettingInfo.my_transports,
			claims: companyAdminSettingInfo.claims,
		};
	});

export const getDataForUpdateCompanyAdminSettingsSelector = () =>
	createSelector(getFormValues(FORM_SETTINGS_ADMIN_NAME), formValues => {
		if (!formValues) return formValues;

		return {
			transports: formValues.transports,
			my_transports: formValues.my_transports,
			claims: formValues.claims,
		};
	});

export const getDataForUpdateCompanyAboutInfoSelector = () =>
	createSelector(getFormValues(FORM_ABOUT_NAME), formValues => {
		if (!formValues) return formValues;

		const transports = [];
		if (formValues.transports && Array.isArray(formValues.transports)) {
			formValues.transports.forEach(item => {
				if (item) {
					const transport = {};

					if (item.car_type_id) {
						transport.car_type_id = Number(item.car_type_id);
					}

					if (item.tonnage_id) {
						transport.tonnage_id = Number(item.tonnage_id);
					}

					if (item.quantity) {
						transport.quantity = Number(item.quantity);
					}

					transports.push(transport);
				}
			});
		}

		const rawPhone = formValues.contact?.phone || '';
		let digits = rawPhone.replace(/\D/g, '');
		if (digits.startsWith('8')) {
			digits = `7${digits.slice(1)}`;
		}

		const data = {
			description: formValues.description || null,
			website: formValues.website || null,
			delivery_methods: formValues.delivery_methods || [],
			transports,
			modes: formValues.modes || [],
			has_validated_transport: formValues.has_validated_transport || false,
			has_qms: formValues.has_qms || false,
			contact: {
				post: formValues.contact?.post || null,
				last_name: formValues.contact?.last_name || null,
				first_name: formValues.contact?.first_name || null,
				email: formValues.contact?.email || null,
				phone: digits || null,
				extension: formValues.contact?.extension || null,
			},
		};

		return {
			...data,
		};
	});
export const getIsCompanyAdminSettingEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && Boolean(query?.id) && query?.mode === 'modules-edit');
	});
export const getIsAdminSettingTabShownSelector = () =>
	createSelector(selectRouterDomain, getIsModalShownSelector(), ({ location: { query } }, isModalShown) => {
		return Boolean(isModalShown && query?.tab === 'modules');
	});

export const getFormCompaniesAboutValuesSelector = () => state => getFormValues(FORM_ABOUT_NAME)(state) || {};

export const getFormSettingIsInvalidSelector = () =>
	createSelector(isInvalid(FORM_SETTINGS_NAME), isInvalid => isInvalid);

export const getFormIsInvalidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);
